import { c as _c } from "react/compiler-runtime";
import { Textarea } from '@systemeio/components';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';
import { SafeHtmlText } from './safe-html-text';
interface FormTextareaProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'className' | 'size'> {
  description?: ReactNode;
  error?: string | null;
  isPreFetching?: boolean;
  label?: ReactNode;
}
const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(function FormTextarea(t0, ref) {
  const $ = _c(42);
  let ariaDescribedBy;
  let description;
  let disabled;
  let error;
  let id;
  let isPreFetching;
  let label;
  let required;
  let textareaProps;
  if ($[0] !== t0) {
    ({
      "aria-describedby": ariaDescribedBy,
      description,
      disabled,
      error,
      id,
      isPreFetching,
      label,
      required,
      ...textareaProps
    } = t0);
    $[0] = t0;
    $[1] = ariaDescribedBy;
    $[2] = description;
    $[3] = disabled;
    $[4] = error;
    $[5] = id;
    $[6] = isPreFetching;
    $[7] = label;
    $[8] = required;
    $[9] = textareaProps;
  } else {
    ariaDescribedBy = $[1];
    description = $[2];
    disabled = $[3];
    error = $[4];
    id = $[5];
    isPreFetching = $[6];
    label = $[7];
    required = $[8];
    textareaProps = $[9];
  }
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const descriptionId = description ? `${textareaId}-description` : undefined;
  const errorId = error ? `${textareaId}-error` : undefined;
  let t1;
  if ($[10] !== ariaDescribedBy || $[11] !== descriptionId || $[12] !== errorId) {
    t1 = [ariaDescribedBy, descriptionId, errorId].filter(Boolean).join(" ") || undefined;
    $[10] = ariaDescribedBy;
    $[11] = descriptionId;
    $[12] = errorId;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  const describedBy = t1;
  const t2 = isPreFetching || undefined;
  const t3 = isPreFetching && "animate-pulse";
  let t4;
  if ($[14] !== t3) {
    t4 = twJoin("grid w-full content-start gap-2", t3);
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== label || $[17] !== required || $[18] !== textareaId) {
    t5 = label !== undefined && label !== null && label !== false ? <label className="text-text-secondary flex" htmlFor={textareaId}>{label}{required ? <span className="text-text-error"> *</span> : null}</label> : null;
    $[16] = label;
    $[17] = required;
    $[18] = textareaId;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const t6 = error ? true : undefined;
  const t7 = error ? "" : undefined;
  const t8 = disabled || isPreFetching;
  let t9;
  if ($[20] !== describedBy || $[21] !== ref || $[22] !== required || $[23] !== t6 || $[24] !== t7 || $[25] !== t8 || $[26] !== textareaId || $[27] !== textareaProps) {
    t9 = <Textarea {...textareaProps} aria-describedby={describedBy} aria-invalid={t6} data-invalid={t7} disabled={t8} id={textareaId} ref={ref} required={required} />;
    $[20] = describedBy;
    $[21] = ref;
    $[22] = required;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = textareaId;
    $[27] = textareaProps;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== description || $[30] !== descriptionId) {
    t10 = description ? <span className="text-text-secondary text-sm" id={descriptionId}>{description}</span> : null;
    $[29] = description;
    $[30] = descriptionId;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== error || $[33] !== errorId) {
    t11 = error ? <SafeHtmlText as="p" className="text-text-error text-sm" html={error} id={errorId} /> : null;
    $[32] = error;
    $[33] = errorId;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t2 || $[38] !== t4 || $[39] !== t5 || $[40] !== t9) {
    t12 = <div aria-busy={t2} className={t4}>{t5}{t9}{t10}{t11}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t2;
    $[38] = t4;
    $[39] = t5;
    $[40] = t9;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  return t12;
});
FormTextarea.displayName = 'FormTextarea';
export { FormTextarea };
export type { FormTextareaProps };